import React, { useEffect, useMemo, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { FaDownload, FaInfoCircle, FaListUl, FaExclamationTriangle } from "react-icons/fa";
import { badgeClass, downloadCsv, formatValue, isNumericType } from "./dataToolsUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

const TEAL = "#0d9488";

// A blank group (e.g. loans with no branch) reads better as "(Not set)" than a dash.
const groupLabel = (value, col) =>
    value === null || value === undefined || value === "" ? "(Not set)" : formatValue(value, col.type, col.bucket);

const StatCard = ({ label, value, accent }) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className={`text-xl font-bold mt-1 ${accent || "text-gray-900"}`}>{value}</p>
    </div>
);

const ResultsPanel = ({ dataset, result, mode, description, loading, error, stale, fileName }) => {
    const [chartMetric, setChartMetric] = useState(null);

    const metricColumns = useMemo(() => (result ? result.columns.filter((c) => c.key.startsWith("m")) : []), [result]);
    const groupColumn = result?.columns.find((c) => c.key === "group");

    useEffect(() => {
        // Prefer charting an amount over a plain count.
        const money = metricColumns.find((c) => c.type === "MONEY");
        setChartMetric((money || metricColumns[0])?.key || null);
    }, [metricColumns]);

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 flex items-start gap-3">
                <FaExclamationTriangle className="mt-1 flex-shrink-0" />
                <div>
                    <p className="font-semibold">We couldn't show these results</p>
                    <p className="text-sm mt-1">{error}</p>
                </div>
            </div>
        );
    }

    if (loading && !result) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-3 animate-pulse">
                {[...Array(6)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded" />)}
            </div>
        );
    }

    if (!result) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-dashed border-gray-300 p-10 text-center text-gray-500">
                <FaListUl className="mx-auto mb-3 text-3xl text-gray-300" />
                <p className="font-medium text-gray-700">Your results will appear here</p>
                <p className="text-sm mt-1">Choose what you'd like to see on the left, then press <b>Show results</b>.</p>
            </div>
        );
    }

    const { columns, rows, totalCount, truncated, totals } = result;
    const isSummary = mode === "SUMMARY";

    // Grand-total row for summaries (sums and counts add up; averages / min / max don't).
    const summaryTotals = isSummary && groupColumn && rows.length > 1
        ? Object.fromEntries(metricColumns
            .filter((c) => /^(Total|Number of)/.test(c.label))
            .map((c) => [c.key, rows.reduce((acc, r) => acc + Number(r[c.key] || 0), 0)]))
        : null;

    const chartCol = metricColumns.find((c) => c.key === chartMetric);
    const showChart = isSummary && groupColumn && rows.length > 1 && chartCol && isNumericType(chartCol.type);
    const chartRows = rows.slice(0, 60);
    const chartData = showChart && {
        labels: chartRows.map((r) => groupLabel(r.group, groupColumn)),
        datasets: [{
            label: chartCol.label,
            data: chartRows.map((r) => Number(r[chartCol.key] || 0)),
            backgroundColor: groupColumn.type === "PERIOD" ? "rgba(13,148,136,0.15)" : TEAL,
            borderColor: TEAL,
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            borderRadius: 4,
        }],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${chartCol.label}: ${formatValue(ctx.parsed.y, chartCol.type)}` } },
        },
        scales: {
            y: { beginAtZero: true, ticks: { callback: (v) => (chartCol.type === "MONEY" ? `₹${Number(v).toLocaleString("en-IN")}` : v) } },
        },
    };

    return (
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
            <div className="bg-teal-50 border border-teal-100 text-teal-900 rounded-xl p-4 text-sm flex items-start gap-3">
                <FaInfoCircle className="mt-0.5 flex-shrink-0 text-teal-600" />
                <div>
                    <p>{description}</p>
                    {stale && <p className="mt-1 text-yellow-800 font-medium">You've changed something — press “Show results” to update.</p>}
                </div>
            </div>

            {/* Headline numbers */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {!isSummary && (
                    <StatCard label={`${dataset.label} found`} value={totalCount.toLocaleString("en-IN")} />
                )}
                {!isSummary && columns.filter((c) => totals[c.key] !== undefined).slice(0, 3).map((c) => (
                    <StatCard key={c.key} label={`Total ${c.label}`} value={formatValue(totals[c.key], "MONEY")} accent="text-teal-700" />
                ))}
                {isSummary && !groupColumn && rows[0] && metricColumns.map((c) => (
                    <StatCard key={c.key} label={c.label} value={formatValue(rows[0][c.key], c.type)} accent="text-teal-700" />
                ))}
                {isSummary && groupColumn && (
                    <StatCard label={`${groupColumn.label} groups`} value={rows.length.toLocaleString("en-IN")} />
                )}
                {summaryTotals && metricColumns.filter((c) => summaryTotals[c.key] !== undefined).slice(0, 3).map((c) => (
                    <StatCard key={c.key} label={`${c.label} (all)`} value={formatValue(summaryTotals[c.key], c.type)} accent="text-teal-700" />
                ))}
            </div>

            {truncated && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-4 py-3 text-sm">
                    {isSummary
                        ? `Showing the first ${rows.length} groups. Add a condition to narrow things down.`
                        : `Showing ${rows.length} of ${totalCount.toLocaleString("en-IN")} matching ${dataset.recordNoun}. The totals above include all of them. Add a condition or show more rows to see the rest.`}
                </div>
            )}

            {showChart && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <h3 className="font-semibold text-gray-800">{chartCol.label} by {groupColumn.label}</h3>
                        {metricColumns.length > 1 && (
                            <select
                                className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white"
                                value={chartMetric}
                                onChange={(e) => setChartMetric(e.target.value)}
                            >
                                {metricColumns.filter((c) => isNumericType(c.type)).map((c) => (
                                    <option key={c.key} value={c.key}>Chart: {c.label}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div className="h-64">
                        {groupColumn.type === "PERIOD"
                            ? <Line data={chartData} options={chartOptions} />
                            : <Bar data={chartData} options={chartOptions} />}
                    </div>
                    {rows.length > chartRows.length && (
                        <p className="text-xs text-gray-500 mt-2">Chart shows the first {chartRows.length} groups; the table below has them all.</p>
                    )}
                </div>
            )}

            <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">
                        {rows.length.toLocaleString("en-IN")} {rows.length === 1 ? "row" : "rows"}
                    </h3>
                    <button
                        onClick={() => downloadCsv(fileName, columns, rows)}
                        disabled={rows.length === 0}
                        className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                        title="Download these rows as a spreadsheet (opens in Excel)"
                    >
                        <FaDownload className="mr-2" /> Download for Excel
                    </button>
                </div>
                {rows.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">
                        <p className="font-medium text-gray-700">Nothing matches these conditions</p>
                        <p className="text-sm mt-1">Try removing a condition or widening a date range.</p>
                    </div>
                ) : (
                    <div className="overflow-auto max-h-screen">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold sticky top-0">
                            <tr>
                                {columns.map((c) => (
                                    <th key={c.key} className={`px-4 py-3 whitespace-nowrap ${isNumericType(c.type) ? "text-right" : ""}`}>{c.label}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                            {rows.map((r, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    {columns.map((c) => (
                                        <td key={c.key} className={`px-4 py-2.5 whitespace-nowrap ${isNumericType(c.type) ? "text-right font-medium text-gray-900" : "text-gray-700"}`}>
                                            {c.type === "ENUM" && r[c.key]
                                                ? <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badgeClass(r[c.key])}`}>{formatValue(r[c.key], c.type)}</span>
                                                : c.key === "group" ? groupLabel(r[c.key], c) : formatValue(r[c.key], c.type, c.bucket)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                            {summaryTotals && (
                                <tfoot className="bg-gray-50 font-bold text-gray-900">
                                <tr>
                                    {columns.map((c) => (
                                        <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${isNumericType(c.type) ? "text-right" : ""}`}>
                                            {c.key === "group" ? "All together" : summaryTotals[c.key] !== undefined ? formatValue(summaryTotals[c.key], c.type) : ""}
                                        </td>
                                    ))}
                                </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResultsPanel;
